import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Check, X, User, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';

const phoneConstraints = {
  '+91': { min: 10, max: 10 },
  '+1': { min: 10, max: 10 },
  '+44': { min: 10, max: 11 },
  '+61': { min: 9, max: 9 },
  '+971': { min: 9, max: 9 },
  '+49': { min: 10, max: 11 },
  '+33': { min: 9, max: 9 },
  '+39': { min: 9, max: 10 },
  '+34': { min: 9, max: 9 },
  '+65': { min: 8, max: 8 },
  '+60': { min: 9, max: 10 },
  '+81': { min: 10, max: 10 },
  '+86': { min: 11, max: 11 },
  '+27': { min: 9, max: 9 },
  '+64': { min: 8, max: 10 },
  '+55': { min: 10, max: 11 },
  '+52': { min: 10, max: 10 },
  '+31': { min: 9, max: 9 },
  '+41': { min: 9, max: 9 },
  '+46': { min: 7, max: 9 },
  '+972': { min: 8, max: 9 },
};

export default function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const getErrorStr = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return fallback;
  };

  // ── Google OAuth registration/login ─────────────────────────────────────
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        // Fetch Google user profile using the access token
        const profileRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });
        const profile = await profileRes.json();

        // Register or login the user in the database
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const dbRes = await fetch(`${apiBase}/api/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: profile.email,
            name: profile.name,
            avatar: profile.picture
          })
        });
        const dbData = await dbRes.json();

        if (dbRes.status === 429) { 
          setErrorMsg('You are creating accounts too fast! Please wait a minute.');
          return;
        }


        if (dbRes.ok && dbData.success) {
          if (dbData.role === 'admin') {
            localStorage.setItem('admin_authenticated', 'true');
            localStorage.setItem('admin_email', dbData.email);
            localStorage.setItem('admin_name', dbData.name || '');
            localStorage.setItem('admin_phone', dbData.phone || '');
            localStorage.setItem('user_role', 'admin');
          } else {
            localStorage.setItem('user_authenticated', 'true');
            localStorage.setItem('user_email', dbData.email);
            localStorage.setItem('user_name', dbData.name || '');
            localStorage.setItem('user_phone', dbData.phone || '');
            localStorage.setItem('user_role', 'user');
          }
          localStorage.setItem('user_avatar', profile.picture || '');

          showToast(`Welcome, ${dbData.name || dbData.email}! 🙏`);
          setTimeout(() => navigate(location.state?.from || (dbData.role === 'admin' ? '/admin' : '/')), 800);
        } else {
          showToast(getErrorStr(dbData.detail, 'Google registration failed on server.'), 'error');
        }
      } catch (err) {
        showToast('Google registration failed. Please try again.', 'error');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      showToast('Google registration was cancelled or failed.', 'error');
    }
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (password !== confirmPassword) {
      setErrorMsg(t('signup.errPasswordMatch', { defaultValue: 'Passwords do not match.' }));
      return;
    }
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!strongPasswordRegex.test(password)) {
      setErrorMsg("Password must be at least 8 characters long and include an uppercase letter, lowercase letter, number, and special character.");
      return;
    }
    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${apiBase}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, phone: `${countryCode} ${phone}` })
      });
      const data = await res.json();
      if (res.status === 429) { 
        setErrorMsg('You are creating accounts too fast! Please wait a minute.');
        return;
      }

      if (res.ok) {
        showToast(t('signup.successReg', { email: data.email, defaultValue: `Account '${data.email}' registered successfully!` }));
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        setErrorMsg(getErrorStr(data.detail, t('signup.errRegFail', { defaultValue: 'Registration failed.' })));
      }
    } catch (err) {
      setErrorMsg(t('signup.errRegOffline', { defaultValue: 'Registration server offline.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        backgroundImage: 'linear-gradient(rgba(13, 13, 13, 0.4), rgba(26, 21, 8, 0.7)), url(/images/portal_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative'
      }}
    >
      {/* Background Blur Overlay */}
      <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(8px)', zIndex: 0 }} />

      {/* Toast Alert */}
      {toast && (
        <div 
          className="animate-fade-in auth-toast"
          style={{
            position: 'fixed',
            top: '90px',
            right: '30px',
            zIndex: 9999,
            backgroundColor: toast.type === 'error' ? 'var(--accent-terracotta)' : 'var(--accent-green)',
            color: '#fff',
            padding: '14px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        >
          {toast.type === 'error' ? <X size={18} /> : <Check size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Back to Home Link */}
      <Link 
        to="/" 
        style={{
          position: 'absolute',
          top: '30px',
          left: '5%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--text-light)',
          textDecoration: 'none',
          fontSize: '0.85rem',
          fontWeight: '600',
          transition: 'var(--transition-fast)',
          zIndex: 1
        }}
        className="hover-gold"
      >
        <ArrowLeft size={16} /> Back to Home
      </Link>

      <div className="glass-card portal-card animate-fade-in" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px' }}>
          <div
            style={{
              width: '75px',
              height: '75px',
              borderRadius: '50%',
              background: '#ffffff',
              border: '2px solid rgba(214,179,106, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              overflow: 'hidden',
              marginBottom: '12px'
            }}
          >
            <img src="/images/new-logo.png" alt="Mystic Routes Logo" style={{ width: '55px', height: '55px', objectFit: 'contain' }} />
          </div>
          <h1 style={{ fontSize: '1.2rem', color: 'var(--primary-gold)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Mystic Routes</h1>
        </div>
        
        <h2 className="font-serif" style={{ fontSize: '1.8rem', color: 'var(--text-light)', marginBottom: '5px' }}>
          {t('signup.title', { defaultValue: 'Portal Registration' })}
        </h2>
        
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '15px' }}>
          {t('signup.subtitle', { defaultValue: 'Create an account to plan journeys, book retreats, and access the portal.' })}
        </p>

        {/* ── Google Button ── */}
        <button
          onClick={() => handleGoogleLogin()}
          disabled={googleLoading}
          className="google-btn"
        >
          {/* Official Google "G" SVG */}
          <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          {googleLoading ? t('signup.btnGoogleLoading', { defaultValue: 'Registering...' }) : t('signup.btnGoogle', { defaultValue: 'Continue with Google' })}
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(181, 137, 37, 0.15)' }} />
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {t('signup.orEmail', { defaultValue: 'or register with email' })}
          </span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(181, 137, 37, 0.15)' }} />
        </div>

        <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div className="portal-input-container">
            <span className="portal-input-icon">
              <User size={18} />
            </span>
            <input 
              type="text" 
              placeholder={t("signup.phName", { defaultValue: "Full Name" })}
              value={name}
              onChange={(e) => setName(e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              required
              className="portal-input"
            />
          </div>

          <div className="portal-input-container">
            <span className="portal-input-icon">
              <Mail size={18} />
            </span>
            <input 
              type="email" 
              placeholder={t("signup.phEmail", { defaultValue: "Email Address" })}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="portal-input"
            />
          </div>

          <div className="portal-input-container" style={{ paddingLeft: 0, overflow: 'visible' }}>
            <select 
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                borderRight: '1px solid rgba(181, 137, 37, 0.25)',
                color: 'var(--text-light)',
                padding: '12px 5px 12px 15px',
                outline: 'none',
                fontSize: '0.9rem',
                cursor: 'pointer',
                borderRadius: '8px 0 0 8px'
              }}
            >
              <option value="+91" style={{ color: '#000' }}>+91 (IN)</option>
              <option value="+1" style={{ color: '#000' }}>+1 (US/CA)</option>
              <option value="+44" style={{ color: '#000' }}>+44 (UK)</option>
              <option value="+61" style={{ color: '#000' }}>+61 (AU)</option>
              <option value="+971" style={{ color: '#000' }}>+971 (AE)</option>
              <option value="+49" style={{ color: '#000' }}>+49 (DE)</option>
              <option value="+33" style={{ color: '#000' }}>+33 (FR)</option>
              <option value="+39" style={{ color: '#000' }}>+39 (IT)</option>
              <option value="+34" style={{ color: '#000' }}>+34 (ES)</option>
              <option value="+65" style={{ color: '#000' }}>+65 (SG)</option>
              <option value="+60" style={{ color: '#000' }}>+60 (MY)</option>
              <option value="+81" style={{ color: '#000' }}>+81 (JP)</option>
              <option value="+86" style={{ color: '#000' }}>+86 (CN)</option>
              <option value="+27" style={{ color: '#000' }}>+27 (ZA)</option>
              <option value="+64" style={{ color: '#000' }}>+64 (NZ)</option>
              <option value="+55" style={{ color: '#000' }}>+55 (BR)</option>
              <option value="+52" style={{ color: '#000' }}>+52 (MX)</option>
              <option value="+31" style={{ color: '#000' }}>+31 (NL)</option>
              <option value="+41" style={{ color: '#000' }}>+41 (CH)</option>
              <option value="+46" style={{ color: '#000' }}>+46 (SE)</option>
              <option value="+972" style={{ color: '#000' }}>+972 (IL)</option>
            </select>
            <input 
              type="tel" 
              placeholder={t("signup.phPhone", { defaultValue: "Phone Number" })}
              value={phone}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '');
                const maxLen = phoneConstraints[countryCode]?.max || 15;
                if (v.length <= maxLen) setPhone(v);
              }}
              required
              minLength={phoneConstraints[countryCode]?.min || 7}
              maxLength={phoneConstraints[countryCode]?.max || 15}
              className="portal-input"
              style={{ paddingLeft: '15px' }}
            />
          </div>

          <div className="portal-input-container">
            <span className="portal-input-icon">
              <Lock size={18} />
            </span>
            <input 
              type="password" 
              placeholder={t("signup.phPassword", { defaultValue: "Password (min 8 chars, strong)" })}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="portal-input"
            />
          </div>

          <div className="portal-input-container">
            <span className="portal-input-icon">
              <Lock size={18} />
            </span>
            <input 
              type="password" 
              placeholder={t("signup.phConfirm", { defaultValue: "Confirm Password" })}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="portal-input"
            />
          </div>

          {errorMsg && (
            <p style={{ color: 'var(--accent-terracotta)', fontSize: '0.8rem', fontWeight: '500', margin: '0' }}>
              {errorMsg}
            </p>
          )}

          <button 
            type="submit" 
            className="btn-gold"
            disabled={loading}
            style={{
              padding: '12px',
              borderRadius: '30px',
              fontSize: '0.85rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginTop: '10px'
            }}
          >
            {loading ? t('signup.btnCreating', { defaultValue: 'Creating...' }) : t('signup.btnRegister', { defaultValue: 'Register' })}
          </button>
        </form>

        <div style={{ marginTop: '15px', borderTop: '1px solid rgba(181, 137, 37, 0.1)', paddingTop: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {t('signup.haveAccount', { defaultValue: 'Already have an account?' })}{' '}
            <Link to="/login" style={{ color: 'var(--primary-gold)', fontWeight: '600', textDecoration: 'none' }}>
              {t('signup.btnSignIn', { defaultValue: 'Sign In Here' })}
            </Link>
          </span>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .auth-toast { top: auto !important; bottom: 20px !important; right: 16px !important; left: 16px !important; width: auto !important; }
        }
      `}</style>
    </div>
  );
}
